import { Trans } from '@lingui/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Header from 'components/ui/Header';
import Segment from 'components/ui/Segment';

import commonPropTypes from 'utils/commonPropTypes';
import capitalizedTranslation from 'utils/i18n';

import * as svars from 'assets/style/variables';

const TooltipContainer = styled.g`
  transition: all 1s linear;
  pointer-events: none;
`;

const TooltipRowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${svars.spaceNormal} ${svars.spaceNormal};
`;

const TooltipRowIndicator = styled.span`
  width: 35%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function TooltipRow({ labelTextId, children }) {
  return (
    <TooltipRowContainer>
      <span>
        <Trans id={labelTextId} render={capitalizedTranslation} />
      </span>
      <TooltipRowIndicator>{children}</TooltipRowIndicator>
    </TooltipRowContainer>
  );
}

TooltipRow.propTypes = {
  labelTextId: commonPropTypes.i18nText.isRequired,
  children: PropTypes.node.isRequired,
};

const TooltipInfoTable = styled(Segment)`
  &&& {
    display: flex;
    flex-direction: column;
    padding-bottom: ${svars.spaceSmall};
  }
`;

function Tooltip({ node, cursorPosition, maxOffsetX, maxOffsetY, elements }) {
  if (
    typeof cursorPosition.offsetX !== 'number' ||
    typeof cursorPosition.offsetY !== 'number'
  ) {
    return null;
  }
  const coords = {
    x: Math.min(
      cursorPosition.offsetX + svars.treemapTooltipCursorDistance,
      maxOffsetX - svars.treemapTooltipWidth
    ),
    y: Math.min(
      cursorPosition.offsetY + svars.treemapTooltipCursorDistance,
      maxOffsetY - svars.treemapTooltipHeight
    ),
  };
  return (
    <TooltipContainer>
      <foreignObject
        width={svars.treemapTooltipWidth}
        height="100%"
        {...coords}
      >
        <TooltipInfoTable>
          <Header
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {node.label}
          </Header>
          {elements.map(({ key, label, Cell }) => (
            <TooltipRow labelTextId={label} key={key}>
              <Cell {...node} value={node[key]} />
            </TooltipRow>
          ))}
        </TooltipInfoTable>
      </foreignObject>
    </TooltipContainer>
  );
}

Tooltip.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    parent_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    n_chunks: PropTypes.number,
    average_sentiment: PropTypes.number,
    share_of_extracts: PropTypes.number,
  }).isRequired,
  cursorPosition: PropTypes.shape({
    offsetX: PropTypes.number,
    offsetY: PropTypes.number,
  }).isRequired,
  maxOffsetX: PropTypes.number.isRequired,
  maxOffsetY: PropTypes.number.isRequired,
  tooltipVolumeLabel: commonPropTypes.i18nText.isRequired,
  tooltipSentimentLabel: commonPropTypes.i18nText.isRequired,
  tooltipShareOfExtractsLabel: commonPropTypes.i18nText.isRequired,
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: commonPropTypes.i18nText,
      Cell: PropTypes.elementType,
    })
  ).isRequired,
};

export default Tooltip;
